import UploadIcon from '@mui/icons-material/Upload';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Link from '@mui/material/Link';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import * as React from 'react';
import { SubmitHandler } from 'react-hook-form';
import { Link as LinkRouter } from 'react-router-dom';
import UserContext from '../../../context/userContext';
import GridWithToolbar from '../../grid/GridWithToolbar';
import { errorToast, successToast } from '../../Toast';
import { TenantsApi } from './api';
import { TenantForm } from './components';
import { TenantDeployForm } from './types';

export default function AdministrationTenants() {
	const { selectedUserRole: user } = React.useContext(UserContext);
	const [uploadProgress, setUploadProgress] = React.useState(0);
	const [
		isDeployTenantConfirmationModalOpen,
		setDeployTenantConfirmationModalOpen,
	] = React.useState(false);

	const [uploading, setUploading] = React.useState(false);
	const TENANTS_KEY = TenantsApi.TENANTS_KEY + '-' + user.namespace;
	const {
		isRefetching,
		isLoading: loading,
		data,
		refetch,
	} = useQuery({ queryKey: [TENANTS_KEY], queryFn: TenantsApi.getTenants });

	const ActiveRolesRow = (params: GridRenderCellParams) => (
		<Link
			underline='hover'
			component={LinkRouter}
			to={`/admin/tenants/${params.row.hostname}${
				params.row.useRolesAndPermissions
					? '/users'
					: '/integrations/external-raters'
			}`}
		>
			{params.value}
		</Link>
	);

	const columns: GridColDef[] = [
		{
			field: 'name',
			headerName: 'Name',
			width: 320,
			renderCell: ActiveRolesRow,
		},
		{
			field: 'locator',
			headerName: 'Locator',
			width: 320,
		},
		{
			field: 'hostname',
			headerName: 'Hostname',
			width: 320,
			flex: 1,
		},
	];

	const handleDeployTenant: SubmitHandler<TenantDeployForm> = async (
		values,
	) => {
		setUploading(true);
		try {
			const formData = new FormData();
			formData.append('zipFile', values.file[0], values.file[0].name);
			formData.append('tenantName', user.namespace + '.' + values.tenantName);
			values.useProductVersioning
				? await TenantsApi.deployTenantWithPV(formData, (e) =>
						setUploadProgress(e),
				  )
				: await TenantsApi.deployTenant(formData, (e) => setUploadProgress(e));

			successToast(
				'Successfully deployed tenant: ' +
					user.namespace +
					'.' +
					values.tenantName,
			);
			refetch();
			setDeployTenantConfirmationModalOpen(false);
		} catch (e) {
			if (axios.isAxiosError(e)) {
				console.error(e);
				errorToast(
					e.response.data.message
						? e.response.data.message
						: 'Error deploying tenant',
				);
			} else {
				errorToast('Error deploying tenant');
			}
		} finally {
			setUploading(false);
		}
	};

	return (
		<>
			<GridWithToolbar
				title='Tenants'
				data={data}
				isRefetching={isRefetching}
				rowId='locator'
				columns={columns}
				loading={loading}
				buttonText='Deploy Tenant'
				buttonIcon={<UploadIcon />}
				onButtonClick={() => setDeployTenantConfirmationModalOpen(true)}
			/>
			<Dialog
				open={isDeployTenantConfirmationModalOpen}
				onClose={() =>
					!uploading && setDeployTenantConfirmationModalOpen(false)
				}
			>
				<DialogTitle>Deploy Tenant</DialogTitle>
				<DialogContent sx={{ width: 600 }}>
					<TenantForm
						isLoading={uploading}
						initialValues={{
							tenantName: '',
							file: null,
							useProductVersioning: false,
						}}
						handleSubmit={handleDeployTenant}
						uploadProgress={uploadProgress}
						handleClose={() => setDeployTenantConfirmationModalOpen(false)}
					/>
				</DialogContent>
			</Dialog>
		</>
	);
}
